<template>
  <CDropdown
    ref="menu"
    inNav
    class="c-header-nav-items preika-account-menu"
    placement="bottom-end"
    add-menu-classes="preika-account-dropdown"
  >
    <template #toggler>
      <CHeaderNavLink class="preika-account-trigger" aria-label="アカウントメニュー">
        <span class="preika-account-avatar">{{ userInitial }}</span>
        <span class="preika-account-copy d-md-down-none">
          <strong>{{ name || 'ユーザー' }}</strong>
          <small :title="roleLabel || null">{{ roleLabel || productName }}</small>
        </span>
        <CIcon name="cil-chevron-bottom" class="preika-account-chevron d-md-down-none"/>
      </CHeaderNavLink>
    </template>

    <CDropdownHeader tag="div" class="preika-account-dropdown__header">
      <span class="preika-account-avatar preika-account-avatar--large">{{ userInitial }}</span>
      <span>
        <strong>{{ name || 'ユーザー' }}</strong>
        <small>{{ roleLabel || (productName + 'メンバー') }}</small>
      </span>
    </CDropdownHeader>

    <CDropdownItem @click="goDashboard">
      <CIcon name="cil-speedometer"/>
      ダッシュボード
    </CDropdownItem>

    <CDropdownItem @click="goPersonalSettings">
      <CIcon name="cil-user"/>
      {{ personalScreenName }}
    </CDropdownItem>

    <CDropdownItem @click="goSecuritySettings">
      <CIcon name="cil-shield-alt"/>
      {{ securityScreenName }}
    </CDropdownItem>

    <CDropdownDivider/>

    <CDropdownItem class="preika-account-dropdown__logout" @click="invalidate">
      <CIcon name="cil-lock-locked"/>
      ログアウト
    </CDropdownItem>

    <!--
      **失敗はメニューの外（画面の上端）に出す**（束23）。

      2026-09-30 まで、失敗の文はこのメニューの中に置き、そのうえで
      ログイン画面へ移していた。メニューごと消えるので文は一度も見えず、
      利用者はログアウトできたと思う。サーバのセッションは残っていて、
      共有端末で同じ画面を開けば入ったままになる。

      いまは失敗を上（TheHeader）へ知らせ、ログイン画面へは移さない。
    -->
  </CDropdown>
</template>

<script>
import axios from 'axios'
import { safeErrorDigest } from '../utils/safeResponseMessage'
import personalSettings from '../services/personalSettings'
import { PRODUCT_NAME } from '../common/productName'
import {
  PERSONAL_SCREEN_NAME,
  SECURITY_SCREEN_NAME
} from '../common/screenNames'

/** ログアウトに失敗したときの文（束23）。画面の上端に出る。 */
const LOGOUT_FAILED = 'ログアウトできませんでした。［ログアウト］をもう一度お試しください。'
  + '解消しない場合は、ログアウト状態にするためブラウザをすべて閉じてください。'

export default {
  name: 'TheHeaderDropdownAccnt',

  data () {
    return {
      name: '',
      /**
       * 名前の下に出す、その人に付いている権限グループの名前（個人設定・ユーザー一覧の
       * 「権限グループ」欄と同じもの）。
       *
       * 以前は GET /authority の旧権限名（common／clerk／admin）をそのまま出していた。
       * 英語の内部の呼び名が画面に出るうえ、旧3区分は権限グループを付け外ししても変わらないので、
       * 社内システム管理者でも admin、一般社員に権限グループを足した人でも common と出ていた
       * （2026-09-26 の撮影）。
       */
      roleNames: []
    }
  },

  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    },

    /*
     * **出す名前と、飛ぶ先の名前を同じものから取る。**
     * 別々に持つと、片方だけ変わっても画面は普通に出る。
     */
    personalScreenName () {
      return PERSONAL_SCREEN_NAME
    },

    securityScreenName () {
      return SECURITY_SCREEN_NAME
    },

    /** 権限グループが複数あるときは、ユーザー管理の確認の小窓と同じく「、」でつなぐ（並びは個人設定と同じ）。 */
    roleLabel () {
      return this.roleNames.join('、')
    },

    userInitial () {
      const value = (this.name || 'P').trim()
      return value ? value.charAt(0).toUpperCase() : 'P'
    }
  },

  mounted () {
    this.fetchAccountInformation()
  },

  methods: {
    /*
     * 名前と権限グループは別々に取る。**片方が取れなくても、もう片方は出す。**
     *
     * 権限グループは個人設定と同じ口（GET /personal-settings の role_names）から取る。
     * 契約が止まっている会社ではこの口が断られるので、そのときは権限グループの代わりに
     * 製品名（予備の表示）を出す。GET /authority はログインと2要素の判定に使う
     * 互換の口なので、ここでは読まない（口は消さない。ダッシュボードの判定が使っている）。
     */
    fetchAccountInformation () {
      axios.get(process.env.VUE_APP_API_ORIGIN + '/name')
        .then(response => {
          this.name = response.data || ''
        })
        .catch(error => {
          console.error('アカウント情報の取得に失敗しました:', safeErrorDigest(error))
        })

      personalSettings.get()
        .then(setting => {
          const names = setting && Array.isArray(setting.role_names) ? setting.role_names : []
          this.roleNames = names.filter(value => typeof value === 'string' && value.trim() !== '')
        })
        .catch(error => {
          console.error('アカウント情報の取得に失敗しました:', safeErrorDigest(error))
        })
    },

    goDashboard () {
      if (this.$route.path !== '/dashboard') {
        this.$router.push('/dashboard')
      }
    },

    goPersonalSettings () {
      if (this.$route.name !== PERSONAL_SCREEN_NAME) {
        this.$router.push({ name: PERSONAL_SCREEN_NAME })
      }
    },

    goSecuritySettings () {
      if (this.$route.name !== SECURITY_SCREEN_NAME) {
        this.$router.push({ name: SECURITY_SCREEN_NAME })
      }
    },

    /**
     * ログアウト。
     *
     * **POST で頼む。**状態を変えるものを GET に置かない。
     * SameSite=Lax は上位遷移の GET に Cookie を送るので、GET のままだと
     * 他所のページから叩かれて、その守りをすり抜ける。
     */
    invalidate () {
      return axios
        .post(process.env.VUE_APP_API_ORIGIN + '/logout')
        .then(() => {
          sessionStorage.clear()
          this.goToLogin()
        })
        .catch(error => {
          console.error('ログアウトに失敗しました:', safeErrorDigest(error))
          /*
           * **ログイン画面へ移さない**（束23）。
           *
           * 以前は「サーバ側が切れているのに画面だけ残ると困る」として、
           * 失敗してもログイン画面へ移していた。ところが失敗のほとんどは
           * 通信が届いていない（サーバのセッションは残っている）場合で、
           * 移すと利用者はログアウトできたと思い込む。共有端末では次の
           * 人がそのまま入れる。
           *
           * 失敗を上（画面の上端）へ知らせ、画面の持ち物も消さない。
           * 本当に切れていれば、次の操作でログイン画面へ送られる。
           *
           * メニューは閉じる。開いたままだと知らせの上に重なって読めない。
           */
          if (this.$refs.menu && typeof this.$refs.menu.hide === 'function') {
            this.$refs.menu.hide()
          }
          this.$emit('logout-failed', LOGOUT_FAILED)
        })
    },

    /**
     * ログイン画面へ移す。
     *
     * <h3>router.push だけに頼らない</h3>
     *
     * **本番で「ログアウトは効くのに画面が変わらない」が起きた。**
     * セッションは切れている（POST /logout は 200）のに、画面の
     * 切り替えだけが失敗していた。
     *
     * vue-router の push は失敗すると Promise を reject する。
     * 同じ場所への遷移、ガードでの打ち切り、**遅れて読み込む画面の
     * ファイルが取れないとき**（配備で js が揃っていないと起きる）。
     * 誰も受け取らないので、押した人からは何も起きないように見える。
     *
     * 失敗したら、ブラウザごと移す。**遷移できたかどうかを、
     * router の都合に任せない。**
     *
     * alert もやめた。閉じるまで次へ進まないので、閉じ忘れると
     * 「押しても何も起きない」と同じ形になる。
     */
    goToLogin () {
      const fallback = () => {
        /* ハッシュ付きの経路。router の基準に合わせる。 */
        window.location.assign('/#/pages/login')
      }

      try {
        const moving = this.$router.push({ name: 'Login' })
        if (moving && typeof moving.catch === 'function') {
          moving.catch(fallback)
        }
      } catch (cannotRoute) {
        fallback()
      }
    }
  }
}
</script>

<style scoped>
.preika-account-menu {
  display: flex;
  align-items: center;
  margin-right: 4px;
}

.preika-account-trigger {
  display: flex;
  min-height: 46px;
  align-items: center;
  gap: 10px;
  padding: 5px 10px !important;
  border: 1px solid var(--preika-gray-200);
  border-radius: 14px;
  background: rgba(255, 255, 255, .88);
  box-shadow: var(--preika-shadow-xs);
  color: var(--preika-text);
  cursor: pointer;
  transition: background var(--preika-transition-fast), border-color var(--preika-transition-fast), box-shadow var(--preika-transition-fast), transform var(--preika-transition-fast);
}

.preika-account-trigger:hover,
.preika-account-trigger:focus,
.preika-account-trigger:focus-within {
  border-color: var(--preika-blue-200);
  background: var(--preika-blue-050);
  box-shadow: 0 8px 20px rgba(15, 42, 64, .08), var(--preika-focus-ring);
  color: var(--preika-text);
  transform: translateY(-1px);
}

.preika-account-avatar {
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: linear-gradient(145deg, var(--preika-blue-600), var(--preika-blue-800));
  box-shadow: 0 7px 15px rgba(10, 102, 178, .2);
  color: #fff;
  font-size: 13px;
  font-weight: 800;
}

.preika-account-avatar--large {
  width: 42px;
  height: 42px;
  flex-basis: 42px;
  border-radius: 13px;
  font-size: 15px;
}

.preika-account-copy {
  min-width: 0;
  text-align: left;
}

.preika-account-copy strong,
.preika-account-copy small,
.preika-account-dropdown__header strong,
.preika-account-dropdown__header small {
  display: block;
}

.preika-account-copy strong {
  max-width: 158px;
  overflow: hidden;
  color: var(--preika-navy-900);
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 権限グループの名前は長くなりうる（複数のときも）。1行にし、あふれたら省略する。全部は title に入れてある。 */
.preika-account-copy small {
  max-width: 158px;
  overflow: hidden;
  margin-top: 1px;
  color: var(--preika-text-muted);
  font-size: 12px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preika-account-chevron {
  width: 12px;
  margin-left: 1px;
  color: var(--preika-gray-400);
}

.preika-account-dropdown__header {
  display: flex;
  min-width: 250px;
  align-items: center;
  gap: 12px;
  padding: 17px 18px;
  background: linear-gradient(180deg, #fff, #f8fafc);
  text-align: left;
}

.preika-account-dropdown__header strong {
  color: var(--preika-navy-900);
  font-size: 12px;
}

.preika-account-dropdown__header small {
  margin-top: 2px;
  color: var(--preika-text-muted);
  font-size: 12px;
}

.preika-account-dropdown__logout {
  color: var(--preika-danger-600);
}

.c-icon {
  margin-right: 9px;
}

@media (max-width: 1199.98px) {
  .preika-account-copy strong,
  .preika-account-copy small {
    max-width: 112px;
  }
}

@media (max-width: 991.98px) {
  .preika-account-trigger {
    min-width: 46px;
    justify-content: center;
    padding: 5px !important;
  }
}
</style>
